/**
 * Canvas-as-Primary-Home — `/workspace/:workspaceId/home`
 * (VISTOERA_CANVAS_AS_PRIMARY_HOME_AND_GLOBAL_ACCOUNT_ENTRY_V1 §4/§6,
 * VistoeraTASK-00581/00582).
 *
 * Fullscreen Canvas shell (WorkspaceLayout treats `/home` as onProductionCanvas)
 * so account-switch never flashes the Workbench sider/header.
 *
 * Resolve order: last-opened (user+workspace, server-validated) → Home Canvas.
 * Also the destination for Workbench V-logo clicks when no return context
 * exists (VistoeraTASK-00573) — Logo must enter Canvas, never toast-block.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'
import { Alert, Button, Spin } from 'antd'
import { useAuth } from '../AuthContext'
import { contentProjectCanvasRoute } from '../navigation/canvasNavigationContext'
import {
  CanvasEntryResolveError,
  resolveCanvasEntryProjectId,
} from './resolveCanvasEntryProject'

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; projectId: string }
  | { kind: 'error'; message: string; retryable: boolean }

export default function WorkspaceHomeEntryPage() {
  const { workspaceId = '' } = useParams()
  const { me } = useAuth()
  const { t } = useTranslation('canvas')
  const userId = me?.user.id
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)
  /** VistoeraTASK-00576 - identity fence: a resolve that finishes after the
   * account / workspace changed (or after a newer resolve started) is dropped,
   * so an old account's project can never be navigated to. */
  const resolveSeq = useRef(0)
  const identityRef = useRef(`${userId ?? ''}::${workspaceId}`)
  identityRef.current = `${userId ?? ''}::${workspaceId}`

  const resolve = useCallback(async () => {
    if (!workspaceId || !userId) return
    const seq = ++resolveSeq.current
    const identity = `${userId}::${workspaceId}`
    const isCurrent = () => seq === resolveSeq.current && identity === identityRef.current
    setPhase({ kind: 'loading' })
    try {
      const projectId = await resolveCanvasEntryProjectId(workspaceId, userId)
      if (!isCurrent()) return
      setPhase({ kind: 'ready', projectId })
    } catch (err) {
      if (!isCurrent()) return
      const e = err as CanvasEntryResolveError
      setPhase({
        kind: 'error',
        message: e.message || t('chrome.homeEntry.openFailed'),
        retryable: e instanceof CanvasEntryResolveError ? e.retryable : true,
      })
    }
  }, [workspaceId, userId, t])

  useEffect(() => {
    void resolve()
  }, [resolve, attempt])

  if (!userId || phase.kind === 'loading') {
    return (
      <div
        className="pcv-fullscreen-shell"
        style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        data-testid="canvas-entry-loading"
      >
        <Spin size="large" />
      </div>
    )
  }

  if (phase.kind === 'error') {
    return (
      <div className="pcv-fullscreen-shell" style={{ margin: 24 }} data-testid="canvas-entry-error">
        <Alert
          type="error"
          showIcon
          message={phase.message}
          action={
            phase.retryable ? (
              <Button size="small" onClick={() => setAttempt((n) => n + 1)}>
                {t('chrome.homeEntry.retry')}
              </Button>
            ) : undefined
          }
        />
      </div>
    )
  }

  return <Navigate to={contentProjectCanvasRoute(workspaceId, phase.projectId)} replace />
}
